import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  Lightning,
  CheckCircle,
  XCircle,
  Calendar,
} from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";
import LoadingScreen from "../../../components/LoadingScreen";

const Dashboard = () => {
  const { companyId, appId } = useParams();
  const AppServices = useAppServices();
  const [loading, setLoading] = useState(true);
  const [appInstalls, setAppInstalls] = useState([]);
  const [appsLogs, setAppsLogs] = useState([]);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { response } = await AppServices.dashboard.crm_genix({
        query: `appId=${appId}&companyId=${companyId}`,
      });
      if (response) {
        setAppInstalls(response.appInstalls || []);
        setAppsLogs(response.appsLogs || []);
      }
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Group Helpers ---
  const groupByMonth = (items, getKey, filterFn) => {
    const counts = {};
    items.forEach((item) => {
      const d = new Date(getKey(item));
      const key = monthNames[d.getMonth()];
      if (!counts[key]) counts[key] = 0;
      if (!filterFn || filterFn(item)) counts[key]++;
    });
    return counts;
  };

  const groupByDay = (items, getKey) => {
    const counts = {};
    items.forEach((item) => {
      const d = new Date(getKey(item));
      const key = `${d.getMonth() + 1}/${d.getDate()}`;
      if (!counts[key]) counts[key] = 0;
      counts[key]++;
    });
    return counts;
  };

  // --- KPI Values ---
  const totalExecutions = appsLogs.length;
  const totalSuccess = appsLogs.filter(
    (m) => m.status?.toLowerCase() === "success"
  ).length;
  const totalFailed = appsLogs.filter(
    (m) => m.status?.toLowerCase() === "failed"
  ).length;

  const today = new Date().toDateString();
  const todayExecutions = appsLogs.filter(
    (m) => new Date(m.createdAt).toDateString() === today
  ).length;

  const kpis = [
    {
      title: "Total Executions",
      value: totalExecutions,
      icon: <Lightning size={32} weight="duotone" color="#f59e0b" />,
    },
    {
      title: "Success",
      value: totalSuccess,
      icon: <CheckCircle size={32} weight="duotone" color="#28a745" />,
    },
    {
      title: "Failed",
      value: totalFailed,
      icon: <XCircle size={32} weight="duotone" color="#dc3545" />,
    },
    {
      title: "Today Executions",
      value: todayExecutions,
      icon: <Calendar size={32} weight="duotone" color="#0d6efd" />,
    },
  ];

  // --- Charts ---
  // Monthly Executions
  const executionsMonthly = groupByMonth(appsLogs, (log) => log.createdAt);
  const executionsSeries = [
    {
      name: "Executions",
      data: monthNames.map((m) => executionsMonthly[m] || 0),
    },
  ];

  // Success vs Failed Over Time (Monthly)
  const executionsMonthlySuccess = groupByMonth(
    appsLogs,
    (log) => log.createdAt,
    (log) => log.status?.toLowerCase() === "success"
  );
  const executionsMonthlyFailed = groupByMonth(
    appsLogs,
    (log) => log.createdAt,
    (log) => log.status?.toLowerCase() === "failed"
  );

  const successFailedMonthlySeries = [
    {
      name: "Success",
      data: monthNames.map((m) => executionsMonthlySuccess[m] || 0),
    },
    {
      name: "Failed",
      data: monthNames.map((m) => executionsMonthlyFailed[m] || 0),
    },
  ];

  // Daily Executions (Last 30 Days)
  const executionsDaily = groupByDay(appsLogs, (log) => log.createdAt);
  const executionsDailySeries = [
    { name: "Executions", data: Object.values(executionsDaily) },
  ];

  // Hourly Executions (Today)
  const hourlyCounts = Array(24).fill(0);
  appsLogs.forEach((log) => {
    const d = new Date(log.createdAt);
    if (d.toDateString() === today) {
      hourlyCounts[d.getHours()]++;
    }
  });
  const hourlySeries = [{ name: "Executions", data: hourlyCounts }];

  // Success Rate
  const totalAttempts = totalSuccess + totalFailed;
  const successRate =
    totalAttempts > 0 ? ((totalSuccess / totalAttempts) * 100).toFixed(1) : 0;

  const successOptions = {
    chart: { type: "donut" },
    labels: ["Delivered", "Failed"],
    legend: { position: "bottom" },
    colors: ["#28a745", "#dc3545"],
  };
  const successSeries = [totalSuccess, totalFailed];

  // Chart Options Factory
  const makeOptions = (categories) => ({
    chart: { toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" },
    legend: { position: "top" },
    xaxis: { categories },
  });
  if (loading) {
    return <LoadingScreen loadingText={"Loading dashboard..."} />;
  }
  return (
    <div className="container-fluid p-4 bg-light min-vh-100">
      {/* KPI Cards */}
      <div className="row g-4 mb-4">
        {kpis.map((kpi, idx) => (
          <div className="col-12 col-sm-6 col-lg-3" key={idx}>
            <div className="card shadow-sm border-0 h-100 p-3 d-flex flex-row align-items-center">
              <div className="me-3">{kpi.icon}</div>
              <div>
                <h6 className="text-muted mb-1">{kpi.title}</h6>
                <h4 className="fw-bold mb-0">{kpi.value}</h4>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">Action Executions (Monthly)</h6>
            <Chart
              options={makeOptions(monthNames)}
              series={executionsSeries}
              type="area"
              height={280}
            />
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100 text-center">
            <h6 className="fw-bold mb-3">Success Rate</h6>
            <Chart
              options={successOptions}
              series={successSeries}
              type="donut"
              height={280}
            />
            <h4 className="fw-bold mt-3 text-success">{successRate}%</h4>
            <p className="text-muted small">Overall delivery success</p>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">Executions (Daily - Last 30 Days)</h6>
            <Chart
              options={makeOptions(Object.keys(executionsDaily))}
              series={executionsDailySeries}
              type="bar"
              height={280}
            />
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">Success vs Failed (Monthly)</h6>
            <Chart
              options={{
                ...makeOptions(monthNames),
                plotOptions: { bar: { stacked: true } },
              }}
              series={successFailedMonthlySeries}
              type="bar"
              height={280}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">Executions (Hourly - Today)</h6>
            <Chart
              options={makeOptions([...Array(24).keys()].map((h) => `${h}:00`))}
              series={hourlySeries}
              type="line"
              height={280}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
